"use client";

import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader, Leaf, CircleCheck } from "lucide-react";
import { smoothScrollToElement, QUOTE_SCROLL_OFFSET, QUOTE_SCROLL_FLAG, QUOTE_SCROLL_FLAG_MAX_AGE_MS } from "@/components/common/QuoteButton";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  postCode: string;
  budgetRange: string;
  timeline: string;
}
const initialValues: FormValues = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address: "",
  postCode: "",
  budgetRange: "",
  timeline: ""
};
export default function GardenInquiryForm() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    let shouldScroll = false;
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("quote") === "1" || window.location.hash === "#gardeninquiryheading") {
      shouldScroll = true;
    }
    try {
      const storedFlag = window.sessionStorage[QUOTE_SCROLL_FLAG];
      if (storedFlag !== undefined) {
        delete window.sessionStorage[QUOTE_SCROLL_FLAG];
        const timestamp = Number(storedFlag);
        if (!Number.isNaN(timestamp) && Date.now() - timestamp < QUOTE_SCROLL_FLAG_MAX_AGE_MS) {
          shouldScroll = true;
        }
      }
    } catch {
      // Ignore sessionStorage errors
    }
    if (shouldScroll) {
      // Start smoothScrollToElement right away
      smoothScrollToElement("gardeninquiryheading", "gardeninquiryform", QUOTE_SCROLL_OFFSET, 10000);

      // And run again from window 'load' listener if document isn't complete yet
      const handleLoad = () => {
        smoothScrollToElement("gardeninquiryheading", "gardeninquiryform", QUOTE_SCROLL_OFFSET, 10000);
      };
      if (document.readyState !== "complete") {
        window.addEventListener("load", handleLoad, {
          once: true
        });
      }
      return () => {
        window.removeEventListener("load", handleLoad);
      };
    }
  }, []);
  return <section id="gardeninquiryform" className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="text-center mb-10">
          <h2 id="gardeninquiryheading" className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4 scroll-mt-6">
            Start Your Garden Transformation
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto font-sans">
            Tell us a bit about your space and vision. We'll review your details and get back to you with the next steps.
          </p>
        </div>

        <Card id="gardeninquirycard" className="border-border/50 shadow-lg bg-muted/10">
          <CardHeader className="pb-4 border-b border-border/30">
            <CardTitle className="text-xl font-default flex items-center gap-2">
              <Leaf className="size-5 text-primary" />
              Project Inquiry Form
            </CardTitle>
            <CardDescription className="font-sans">
              Please complete all sections to help us understand your needs.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <InquiryFormContent />
          </CardContent>
        </Card>
      </div>
    </section>;
}
function InquiryFormContent() {
  const [formDef, setFormDef] = useState<any>(null);
  const [values, setValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>("Thank you for your inquiry! Our design team will review your details and contact you shortly.");
  const [hpValue, setHpValue] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fetchTimestampRef = useRef<number>(Date.now());
  const fetchDefinition = async () => {
    const client = getWvcClient();
    if (!client?.pluginRequest) return;
    try {
      const res = await client.pluginRequest("wvc-forms/v1/forms/garden-inquiry-main");
      setFormDef(res);
      fetchTimestampRef.current = Date.now();
    } catch (err: any) {
      if (err?.status === 404) {
        try {
          const listRes = await client.pluginRequest("wvc-forms/v1/forms");
          const items = listRes?.items || [];
          const matched = items.find((item: any) => item.key === "garden-inquiry-main") || (items.length === 1 ? items[0] : null) || items.find((item: any) => item.name?.toLowerCase().includes("garden") || item.name?.toLowerCase().includes("inquiry"));
          if (matched?.key) {
            const fallbackDef = await client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
            setFormDef(fallbackDef);
            fetchTimestampRef.current = Date.now();
          }
        } catch {
          // Keep formDef null
        }
      }
    }
  };
  useEffect(() => {
    fetchDefinition();
  }, []);
  const handleChange = (field: keyof FormValues, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => {
        const next = {
          ...prev
        };
        delete next[field];
        return next;
      });
    }
  };
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    setUploadedFileId(null);
    setUploadError(null);
    if (errors.mediaUpload) {
      setErrors(prev => {
        const next = {
          ...prev
        };
        delete next.mediaUpload;
        return next;
      });
    }
    if (!file) return;
    const client = getWvcClient();
    if (!client?.pluginUpload) return;
    const fileField = formDef?.fields?.find((f: any) => {
      const k = (f.key || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      return k === "mediaupload" || k === "media" || f.type === "file";
    });
    if (fileField && formDef?.spam?.token) {
      try {
        setIsUploadingFile(true);
        const formKey = formDef?.key || "garden-inquiry-main";
        const res = await client.pluginUpload(`wvc-forms/v1/forms/${formKey}/upload`, {
          file,
          fields: {
            token: formDef.spam.token,
            field: fileField.id
          }
        });
        if (res?.success && res.file_id) {
          setUploadedFileId(res.file_id);
        } else {
          setUploadError(res?.message || "Failed to upload file");
        }
      } catch (err: any) {
        setUploadError(err?.message || "Failed to upload file");
      } finally {
        setIsUploadingFile(false);
      }
    }
  };
  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!values.firstName.trim() || values.firstName.trim().length < 2) {
      nextErrors.firstName = "First name is required";
    }
    if (!values.lastName.trim() || values.lastName.trim().length < 2) {
      nextErrors.lastName = "Last name is required";
    }
    if (!values.email.trim()) {
      nextErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(values.email.trim())) {
      nextErrors.email = "Invalid email address";
    }
    if (!values.phone.trim() || values.phone.trim().length < 10) {
      nextErrors.phone = "Phone number is required";
    }
    if (!values.address.trim()) {
      nextErrors.address = "Address is required";
    }
    if (!values.postCode.trim()) {
      nextErrors.postCode = "Post code is required";
    }
    if (!values.budgetRange) {
      nextErrors.budgetRange = "Please select a budget range";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate()) {
      return;
    }
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setSubmitError("Unable to submit form. Please check your connection and try again.");
      return;
    }
    const formKey = formDef?.key || "garden-inquiry-main";
    let fileIdToSubmit = uploadedFileId;
    if (selectedFile && !fileIdToSubmit && client?.pluginUpload) {
      const fileField = formDef?.fields?.find((f: any) => {
        const k = (f.key || "").toLowerCase().replace(/[^a-z0-9]/g, "");
        return k === "mediaupload" || k === "media" || f.type === "file";
      });
      if (fileField && formDef?.spam?.token) {
        try {
          setIsUploadingFile(true);
          const upRes = await client.pluginUpload(`wvc-forms/v1/forms/${formKey}/upload`, {
            file: selectedFile,
            fields: {
              token: formDef.spam.token,
              field: fileField.id
            }
          });
          if (upRes?.success && upRes.file_id) {
            fileIdToSubmit = upRes.file_id;
            setUploadedFileId(upRes.file_id);
          }
        } catch {
          // Continue with submit attempt
        } finally {
          setIsUploadingFile(false);
        }
      }
    }
    const minSeconds = formDef?.spam?.min_seconds || 3;
    const elapsedSeconds = (Date.now() - fetchTimestampRef.current) / 1000;
    if (elapsedSeconds < minSeconds) {
      await new Promise(resolve => setTimeout(resolve, (minSeconds - elapsedSeconds) * 1000));
    }
    setIsSubmitting(true);
    const keyToValueMap: Record<string, any> = {
      firstname: values.firstName,
      lastname: values.lastName,
      email: values.email,
      phone: values.phone,
      address: values.address,
      postcode: values.postCode,
      post_code: values.postCode,
      budgetrange: values.budgetRange,
      budget_range: values.budgetRange,
      timeline: values.timeline,
      mediaupload: fileIdToSubmit ? [fileIdToSubmit] : [],
      media_upload: fileIdToSubmit ? [fileIdToSubmit] : [],
      media: fileIdToSubmit ? [fileIdToSubmit] : []
    };
    const payloadValues: Record<string, any> = {};
    if (formDef?.fields && Array.isArray(formDef.fields)) {
      formDef.fields.forEach((field: any) => {
        const rawKey = (field.key || "").toLowerCase();
        const normKey = rawKey.replace(/[^a-z0-9]/g, "");
        if (field.type === "file") {
          if (fileIdToSubmit) {
            payloadValues[field.id] = [fileIdToSubmit];
          }
          return;
        }
        const match = Object.entries(keyToValueMap).find(([k]) => {
          return k === rawKey || k.replace(/[^a-z0-9]/g, "") === normKey;
        });
        if (match !== undefined) {
          payloadValues[field.id] = match[1];
        }
      });
    } else {
      Object.entries(keyToValueMap).forEach(([k, v]) => {
        payloadValues[k] = v;
      });
    }
    const hpData = formDef?.spam?.honeypot ? {
      [formDef.spam.honeypot.field]: hpValue
    } : undefined;
    try {
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formKey}/submit`, {
        method: "POST",
        data: {
          values: payloadValues,
          token: formDef?.spam?.token,
          hp: hpData,
          meta: {
            source_url: typeof window !== "undefined" ? window.location.href : "",
            referrer: typeof document !== "undefined" ? document.referrer : ""
          }
        }
      });
      if (res.success) {
        if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
          window.location.assign(res.confirmation.url);
          return;
        }
        if (res.confirmation?.message) {
          const stripped = res.confirmation.message.replace(/<[^>]+>/g, "").trim();
          setSuccessMessage(stripped || res.confirmation.message);
        }
        setIsSubmitted(true);
      } else if (res.code === "frms_validation_failed") {
        if (res.errors) {
          const mappedErrors: Record<string, string> = {};
          if (formDef?.fields) {
            formDef.fields.forEach((f: any) => {
              if (res.errors[f.id]) {
                const k = (f.key || "").toLowerCase().replace(/[^a-z0-9]/g, "");
                if (k.includes("first")) mappedErrors.firstName = res.errors[f.id].message;else if (k.includes("last")) mappedErrors.lastName = res.errors[f.id].message;else if (k.includes("email")) mappedErrors.email = res.errors[f.id].message;else if (k.includes("phone")) mappedErrors.phone = res.errors[f.id].message;else if (k.includes("address")) mappedErrors.address = res.errors[f.id].message;else if (k.includes("post")) mappedErrors.postCode = res.errors[f.id].message;else if (k.includes("budget")) mappedErrors.budgetRange = res.errors[f.id].message;else if (k.includes("timeline")) mappedErrors.timeline = res.errors[f.id].message;else if (k.includes("media") || f.type === "file") mappedErrors.mediaUpload = res.errors[f.id].message;
              }
            });
          }
          setErrors(mappedErrors);
        }
        setSubmitError(res.message || "Please check the highlighted fields.");
      } else if (res.code === "frms_invalid_token") {
        await fetchDefinition();
        setSubmitError("Session expired. Please try submitting again.");
      } else {
        setSubmitError(res.message || "An error occurred while submitting your enquiry.");
      }
    } catch (err: any) {
      setSubmitError(err?.message || "Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const resetForm = () => {
    setValues(initialValues);
    setErrors({});
    setSelectedFile(null);
    setUploadedFileId(null);
    setUploadError(null);
    setIsSubmitted(false);
    setSubmitError(null);
    fetchDefinition();
  };
  if (isSubmitted && !submitError) {
    return <div className="py-12 flex flex-col items-center justify-center text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold font-default text-foreground">Inquiry Received</h3>
        <p className="text-muted-foreground font-sans max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Submit Another Project
        </Button>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="space-y-6">
      {formDef?.spam?.honeypot && <input type="text" name={formDef.spam.honeypot.field} value={hpValue} onChange={e => setHpValue(e.target.value)} tabIndex={-1} autoComplete="off" style={{
      position: "absolute",
      left: "-9999px"
    }} aria-hidden="true" />}

      <Accordion type="single" collapsible defaultValue="step-1" className="w-full space-y-4">
        {/* Step 1: Basic Details */}
        <AccordionItem value="step-1" className="bg-card border border-border rounded-lg px-4 data-[state=open]:shadow-sm transition-all">
          <AccordionTrigger className="hover:no-underline py-4">
            <div className="flex items-center gap-3 text-base font-semibold font-default">
              <span className="flex items-center justify-center size-6 rounded-lg bg-primary/10 text-primary text-xs">1</span>
              Basic Details
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-2 pb-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <label className="text-sm font-medium leading-none">First Name</label>
                <Input placeholder="John" value={values.firstName} onChange={e => handleChange("firstName", e.target.value)} aria-invalid={!!errors.firstName} />
                {errors.firstName && <p className="text-destructive text-sm font-medium">{errors.firstName}</p>}
              </div>
              <div className="grid gap-2">
                <label className="text-sm font-medium leading-none">Last Name</label>
                <Input placeholder="Doe" value={values.lastName} onChange={e => handleChange("lastName", e.target.value)} aria-invalid={!!errors.lastName} />
                {errors.lastName && <p className="text-destructive text-sm font-medium">{errors.lastName}</p>}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <label className="text-sm font-medium leading-none">Email Address</label>
                <Input type="email" placeholder="john@example.com" value={values.email} onChange={e => handleChange("email", e.target.value)} aria-invalid={!!errors.email} />
                {errors.email && <p className="text-destructive text-sm font-medium">{errors.email}</p>}
              </div>
              <div className="grid gap-2">
                <label className="text-sm font-medium leading-none">Phone Number</label>
                <Input type="tel" placeholder="07700 900000" value={values.phone} onChange={e => handleChange("phone", e.target.value)} aria-invalid={!!errors.phone} />
                {errors.phone && <p className="text-destructive text-sm font-medium">{errors.phone}</p>}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Step 2: Location */}
        <AccordionItem value="step-2" className="bg-card border border-border rounded-lg px-4 data-[state=open]:shadow-sm transition-all">
          <AccordionTrigger className="hover:no-underline py-4">
            <div className="flex items-center gap-3 text-base font-semibold font-default">
              <span className="flex items-center justify-center size-6 rounded-lg bg-primary/10 text-primary text-xs">2</span>
              Location
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-2 pb-6 space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div className="grid gap-2">
                <label className="text-sm font-medium leading-none">Address</label>
                <Input placeholder="123 Main St" value={values.address} onChange={e => handleChange("address", e.target.value)} aria-invalid={!!errors.address} />
                {errors.address && <p className="text-destructive text-sm font-medium">{errors.address}</p>}
              </div>
              <div className="grid gap-2">
                <label className="text-sm font-medium leading-none">Post Code</label>
                <Input placeholder="AB12 3CD" value={values.postCode} onChange={e => handleChange("postCode", e.target.value)} aria-invalid={!!errors.postCode} />
                {errors.postCode && <p className="text-destructive text-sm font-medium">{errors.postCode}</p>}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Step 3: Budget & Timing */}
        <AccordionItem value="step-3" className="bg-card border border-border rounded-lg px-4 data-[state=open]:shadow-sm transition-all">
          <AccordionTrigger className="hover:no-underline py-4">
            <div className="flex items-center gap-3 text-base font-semibold font-default">
              <span className="flex items-center justify-center size-6 rounded-lg bg-primary/10 text-primary text-xs">3</span>
              Budget & Timing
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-2 pb-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <label className="text-sm font-medium leading-none">Estimated Budget</label>
                <Select value={values.budgetRange} onValueChange={val => handleChange("budgetRange", val)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select budget..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under-5k">Under £5,000</SelectItem>
                    <SelectItem value="5k-15k">£5,000 - £15,000</SelectItem>
                    <SelectItem value="15k-30k">£15,000 - £30,000</SelectItem>
                    <SelectItem value="30k-50k">£30,000 - £50,000</SelectItem>
                    <SelectItem value="over-50k">Over £50,000</SelectItem>
                  </SelectContent>
                </Select>
                {errors.budgetRange && <p className="text-destructive text-sm font-medium">{errors.budgetRange}</p>}
              </div>
              <div className="grid gap-2">
                <label className="text-sm font-medium leading-none">Desired Start Time</label>
                <Select value={values.timeline} onValueChange={val => handleChange("timeline", val)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select timeline..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asap">As soon as possible</SelectItem>
                    <SelectItem value="1-3-months">1-3 months</SelectItem>
                    <SelectItem value="3-6-months">3-6 months</SelectItem>
                    <SelectItem value="flexible">Flexible</SelectItem>
                  </SelectContent>
                </Select>
                {errors.timeline && <p className="text-destructive text-sm font-medium">{errors.timeline}</p>}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Step 4: Garden Photos/Videos */}
        <AccordionItem value="step-4" className="bg-card border border-border rounded-lg px-4 data-[state=open]:shadow-sm transition-all">
          <AccordionTrigger className="hover:no-underline py-4">
            <div className="flex items-center gap-3 text-base font-semibold font-default">
              <span className="flex items-center justify-center size-6 rounded-lg bg-primary/10 text-primary text-xs">4</span>
              Garden Photos/Videos
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-2 pb-6 space-y-4">
            <div className="grid gap-2">
              <label className="text-sm font-medium leading-none">Upload Media</label>
              <Input type="file" accept="image/*,video/*" onChange={handleFileChange} />
              <p className="text-muted-foreground text-sm">Upload photos or videos to help us understand your garden better</p>
              {uploadError && <p className="text-destructive text-sm font-medium">{uploadError}</p>}
              {errors.mediaUpload && <p className="text-destructive text-sm font-medium">{errors.mediaUpload}</p>}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {submitError && <div className="p-4 mt-6 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <div className="mt-8 pt-6 border-t border-border/50">
        <Button type="submit" size="lg" className="w-full md:w-auto md:min-w-[200px] text-base font-semibold" disabled={isSubmitting || isUploadingFile}>
          {isSubmitting || isUploadingFile ? <>
              <Loader className="animate-spin mr-2 size-5" />
              {isUploadingFile ? "Uploading..." : "Submitting..."}
            </> : 'Submit Inquiry'}
        </Button>
        <p className="text-xs text-muted-foreground mt-4 text-center md:text-left">
          By submitting this form, you agree to our privacy policy and terms of service. We will never share your details with third parties.
        </p>
      </div>
    </form>;
}